"use client";

import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Review {
  name: string;
  title: string;
  text: string;
}

export default function TestimonialsCarousel({
  reviews,
}: {
  reviews: Review[];
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <div>
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-8">
          {reviews.map((review, i) => (
            <div
              key={i}
              className="flex flex-col p-8 rounded-[2.5rem] border border-border/50 bg-card hover:shadow-lg hover:border-primary/30 transition-all duration-300 shrink-0 grow-0 basis-full md:basis-1/2 lg:basis-1/3">
              <div className="flex gap-1 text-primary mb-6">
                {[...Array(5)].map((_, star) => (
                  <svg
                    key={star}
                    className="w-5 h-5 fill-current"
                    viewBox="0 0 24 24">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                ))}
              </div>
              <p className="font-body text-muted-foreground italic text-lg leading-relaxed mb-8 grow">
                &quot;{review.text}&quot;
              </p>
              <div className="flex items-center gap-4 mt-auto">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                  {review.name.charAt(0)}
                </div>
                <div>
                  <p className="font-heading font-bold text-foreground text-lg">
                    {review.name}
                  </p>
                  <p className="text-sm font-body text-muted-foreground mt-0.5">
                    {review.title}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center gap-6 mt-10">
        <button
          type="button"
          onClick={scrollPrev}
          aria-label="Previous testimonial"
          className="p-3 rounded-full border border-border/50 hover:bg-primary/10 hover:border-primary/30 transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex gap-2">
          {reviews.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to testimonial ${i + 1}`}
              onClick={() => emblaApi?.scrollTo(i)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                i === selectedIndex ? "bg-primary" : "bg-muted"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={scrollNext}
          aria-label="Next testimonial"
          className="p-3 rounded-full border border-border/50 hover:bg-primary/10 hover:border-primary/30 transition-colors">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
